import { useEffect, useState, useContext } from "react";
import HeaderAppBar from "./appbar";
import { Snackbar, Alert } from "@mui/material";
import { OrderSummary } from "./order_summary";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Typography,
  TextField,
} from "@mui/material";
import type { AlertColor } from "@mui/material";
import { CallActionButton, WhatsappActionButton } from "./floating_buttons";
import { AuthContext, BackendBaseUrl, FrontEndBaseUrl, SrinuPick, ShankarPick, NanaajiPick, ShankarID, SrinuId, NanaajiId, ORANGE, ORANGE_HOVER, PREMIUM_SHADOW, SUCCESS_GREEN, SUCCESS_GREEN_HOVER, ERROR_RED, ERROR_RED_HOVER } from "./global_varibles";
import { useNavigate, useParams } from "react-router-dom";
import PercentIcon from "@mui/icons-material/Percent";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LinearProgress from "@mui/material/LinearProgress";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import { alpha } from "@mui/material/styles";



// Simple ProgressTracker component with professional styling
function ProgressTracker({ currentPercentage }: { currentPercentage: number }) {
  return (
    <Box sx={{ width: "100%", marginY: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#0b0f14" }}>
          Order Progress
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 600, color: ORANGE }}>
          {currentPercentage}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={currentPercentage}
        sx={{ 
          height: 10, 
          borderRadius: 5,
          backgroundColor: alpha(ORANGE, 0.1),
          '& .MuiLinearProgress-bar': {
            backgroundColor: ORANGE,
            borderRadius: 5,
            backgroundImage: `linear-gradient(90deg, ${ORANGE} 0%, ${ORANGE_HOVER} 100%)`,
          }
        }}
      />
    </Box>
  );
}

// Types matching order_summary props
type CategoryDetails = {
  item_price: number;
  quantity: number;
  type_id?: string | number;
  serv_id?: string | number;
};

type OrderedItem = {
  item_name: string;
  item_url: string;
  categories: Record<string, CategoryDetails>;
};

type OrderedDetailsType = {
  [serviceType: string]: {
    [itemId: string]: OrderedItem;
  };
};

type NewItemType = {
  itemName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
};

type PmtStatus = "paid" | "semi-paid" | "not paid";

interface OrderInfo {
  order_id: number | string;
  total_price: number;
  other_charges: number;
  discount: number;
  pmt_status: PmtStatus;
  balance_amt?: number;
  status: boolean;
  per_cov: number;
  recieved_on?: string;
  to_be_delivered_on?: string;
}

interface CustomerDetails {
  name: string;
  phone: string;
  address: string;
}

interface OrderDetailsResponse {
  status: "success" | "error";
  order_details?: {
    order_details: OrderInfo;
    ordered_items: OrderedDetailsType;
    customer_details: CustomerDetails;
    others: NewItemType[];
  };
  message?: string;
}

function ShowSavedOrderDetails() {
  const { user_data, isAuthinticated } = useContext(AuthContext) ?? { user_data: undefined, isAuthinticated: false };
  const { orderId } = useParams<{ orderId: string }>();
  const [loading, setLoading] = useState(true);
  const [ordered_details, setOrderedDetails] = useState<OrderedDetailsType>({});
  const [new_items, setNewItems] = useState<NewItemType[]>([]);
  const [user_details, setUserDeatils] = useState<CustomerDetails>({ name: "", phone: "", address: "" });
  const [order_info, setOrderInfo] = useState<OrderInfo>({
    order_id: "",
    total_price: 0,
    other_charges: 0,
    discount: 0,
    pmt_status: "not paid",
    balance_amt: 0,
    status: false,
    per_cov: 0,
    recieved_on: undefined,
    to_be_delivered_on: undefined,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pmtdialogOpen, setPmtDialogOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<string>("");
  const [editableTotal, setEditableTotal] = useState<number>(0);
  const [total_order_value, setOrderValue] = useState<number>(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

  const navigate = useNavigate();

  const openSnackbar = (message: string, severity: AlertColor) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const closeSnackbar = () => setSnackbarOpen(false);

  const calculateTotalValue = (info: OrderInfo) => {
    return parseFloat((info.total_price + info.other_charges - (info.discount / 100) * (info.total_price + info.other_charges)).toFixed(2));
  };

  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        const backend_url = `${BackendBaseUrl}/order-details?order_id=${orderId}&is_admin=${isAuthinticated}`;
        const response = await fetch(backend_url, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        const response_data = (await response.json()) as OrderDetailsResponse;

        if (response_data.status === "success" && response_data.order_details) {
          const basic_order_details = response_data.order_details.order_details;
          setOrderedDetails(response_data.order_details.ordered_items);
          setUserDeatils(response_data.order_details.customer_details);
          setNewItems(response_data.order_details.others);
          setOrderInfo(basic_order_details);

          const total_order_val = calculateTotalValue(basic_order_details);
          setOrderValue(total_order_val);

          if (basic_order_details.pmt_status === "semi-paid") {
            setEditableTotal(basic_order_details.balance_amt ?? 0);
          } else {
            setEditableTotal(total_order_val);
          }
        } else {
          console.error("Error in fetching data");
        }
      } catch (error) {
        console.error("Error fetching order details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderData();
  }, [orderId, isAuthinticated]);

  if (loading) {
    return (
      <Box>
        <HeaderAppBar />
        <Box sx={{ textAlign: "center", marginTop: 10 }}>
          <Typography variant="h6" sx={{ color: "#64748b" }}>Loading order details...</Typography>
        </Box>
      </Box>
    );
  }

  const formattedRecievedOn = order_info.recieved_on ? new Date(order_info.recieved_on).toLocaleDateString() : "";
  const formattedToBeDeliveredOn = order_info.to_be_delivered_on ? new Date(order_info.to_be_delivered_on).toLocaleDateString() : "";

  const form_user_data: Record<string, string> = {
    Name: user_details.name || "",
    Phone: user_details.phone || "",
    Address: user_details.address || "",
    "Received On": formattedRecievedOn,
    "To Be Delivered On": formattedToBeDeliveredOn,
  };
  const filteredUserData = Object.entries(form_user_data).filter(([, value]) => value);

  const HandleReadyToDeliver = async () => {
    try {
      const BackendRoute = `${BackendBaseUrl}create-order`;
      const response = await fetch(BackendRoute, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          req_id: user_data?.id,
          ord_id: order_info.order_id,
          order_details: { ready_to_deliver: true },
        }),
      });
      const response_data = (await response.json()) as { status: "success" | "error"; message?: string };
      if (response_data.status === "success") {
        setOrderInfo((prevValues) => ({ ...prevValues, per_cov: 75 }));
        openSnackbar("Order marked as ready to deliver", "success");
      } else {
        openSnackbar("Failed to update order status", "error");
      }
    } catch (error) {
      openSnackbar("Error updating order status", "error");
    }
  };

  const handleTotalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditableTotal(Number(e.target.value));
  };

  const HandleDeleteOrder = async () => {
    if (!confirm("Are you sure you want to delete this order? This action cannot be undone.")) {
      return;
    }

    try {
      const BackendRoute = `${BackendBaseUrl}create-order`;
      const response = await fetch(BackendRoute, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ord_id: order_info.order_id,
          req_id: user_data?.id,
        }),
      });
      const response_data = (await response.json()) as { message?: string };
      const message = response_data.message ?? "Order deleted successfully";
      if (response.ok) {
        openSnackbar(message, "success");
        setTimeout(() => navigate("/"), 1500);
      } else {
        openSnackbar(message, "error");
      }
    } catch (error) {
      openSnackbar("Failed to delete order", "error");
    }
  };

  const HandleDeliver = async () => {
    if (!selectedPerson) {
      alert("Please select a person to complete delivery");
      return;
    }
    try {
      const BackendRoute = `${BackendBaseUrl}create-order`;
      const response = await fetch(BackendRoute, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ord_id: order_info.order_id,
          req_id: user_data?.id,
          order_details: {
            status: true,
            paid_to: selectedPerson,
            amnt_paid: Number(editableTotal),
          },
        }),
      });
      const response_data = (await response.json()) as { status: "success" | "error"; message?: string };
      const message = response_data.message ?? "";
      if (response_data.status === "success") {
        openSnackbar(message || "Order delivered successfully", "success");
        setOrderInfo((prevValues) => ({ ...prevValues, status: true, per_cov: 100 }));
        setTimeout(() => window.location.reload(), 1500);
      } else {
        openSnackbar(message || "Failed to deliver order", "error");
      }
    } catch (error) {
      openSnackbar("Failed to deliver order", "error");
    } finally {
      handleDialogClose();
    }
  };

  const message =
    `Dear ${user_details.name},\n` +
    `Thank you for using MuftyKare for your laundry needs!\n` +
    `Here's the summary of your bill\n${FrontEndBaseUrl}order-details/${order_info.order_id}\n\n` +
    `We appreciate your trust in MuftyKare and look forward to serving you again!\n\n` +
    `Warm regards,\nThe MuftyKare Team`;

  const handleDialogOpen = () => setDialogOpen(true);
  const handleDialogClose = () => setDialogOpen(false);
  const PaymentComponentClose = () => setPmtDialogOpen(false);

  const handlePaymentClick = (method: "paytm" | "gpay" | "phonepe") => {
    const upiUrls: Record<"paytm" | "gpay" | "phonepe", string> = {
      paytm: `paytmmp://pay?pa=9542926041@ptsbi&pn=yaswanth&tn=muftykare laundary services&am=${editableTotal}&cu=INR&tr=${Date.now()}`,
      gpay: `tez://upi/pay?pa=yaswanthapswr1234@oksbi&pn=yaswanth&tn=muftykare laundary services&am=${editableTotal}&cu=INR&tr=${Date.now()}`,
      phonepe: `phonepe://pay?pa=yaswanthkotyada@ibl&pn=yaswanth&tn=muftykare laundary services&am=${editableTotal}&cu=INR&tr=${Date.now()}`,
    };
    const pmt_url = upiUrls[method];
    if (pmt_url) window.location.href = pmt_url;
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedPerson(event.target.value);
  };

  const ReRouteToEditPage = () => {
    localStorage.removeItem("edit_current_order_items");
    localStorage.removeItem("structuredData");
    localStorage.removeItem("edit_user_details");
    localStorage.removeItem("edit_new_items");
    navigate(`/edit-order/${orderId}`);
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f8fafc" }}>
      <HeaderAppBar />
      
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 1200,
          mx: "auto",
          mt: 2,
          p: { xs: 2, sm: 4 },
          borderRadius: { xs: 0, sm: 4 },
          border: `1px solid ${alpha(ORANGE, 0.1)}`,
          boxShadow: PREMIUM_SHADOW,
          backgroundColor: "#fff",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${ORANGE} 0%, ${ORANGE_HOVER} 100%)`,
          }
        }}
      >
        {/* Header with back button and title */}
        <Box sx={{ position: "relative", mb: 3 }}>
          {isAuthinticated && (
            <Button
              onClick={() => navigate(-1)}
              startIcon={<ChevronLeftRoundedIcon />}
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                color: "#0b0f14",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 20,
                px: 1.25,
                "&:hover": { backgroundColor: alpha(ORANGE, 0.1), color: ORANGE }
              }}
            >
              Back
            </Button>
          )}

          <Typography variant="h4" align="center" sx={{ fontWeight: 700, color: "#0b0f14" }}>
            Bill #{order_info.order_id}
          </Typography>
        </Box>

        {/* Customer Details Card */}
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            mb: 3,
            borderColor: alpha(ORANGE, 0.2),
            borderRadius: 2,
            backgroundColor: alpha(ORANGE, 0.02)
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "#0b0f14" }}>
            Customer Details
          </Typography>
          <Box sx={{ 
            display: "grid", 
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, 
            gap: 2 
          }}>
            {filteredUserData.map(([key, value]) => (
              <Box key={key}>
                <Typography variant="caption" sx={{ color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {key}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: "#0b0f14" }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Progress Tracker */}
        <ProgressTracker currentPercentage={order_info.per_cov} />

        {/* Floating Action Buttons */}
        <Box
          sx={{
            position: "fixed",
            top: { xs: "30%", sm: "50%" },
            right: { xs: 12, sm: 16 },
            transform: "translateY(-50%)",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            zIndex: 1000,
          }}
        >
          {isAuthinticated && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {/* Call Button */}
              <Box
                sx={{
                  position: "relative",
                  "&:hover .tooltip": {
                    opacity: 1,
                    transform: "translateX(-8px)",
                  }
                }}
              >
                <Typography
                  className="tooltip"
                  variant="caption"
                  sx={{
                    position: "absolute",
                    right: "100%",
                    top: "50%",
                    transform: "translateY(-50%) translateX(8px)",
                    backgroundColor: "#1f2937",
                    color: "white",
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    opacity: 0,
                    transition: "all 0.2s ease-in-out",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: "100%",
                      top: "50%",
                      transform: "translateY(-50%)",
                      border: "4px solid transparent",
                      borderLeftColor: "#1f2937",
                    }
                  }}
                >
                  Call Customer
                </Typography>
                <CallActionButton phone_number={user_details.phone} />
              </Box>

              {/* WhatsApp Button */}
              <Box
                sx={{
                  position: "relative",
                  "&:hover .tooltip": {
                    opacity: 1,
                    transform: "translateX(-8px)",
                  }
                }}
              >
                <Typography
                  className="tooltip"
                  variant="caption"
                  sx={{
                    position: "absolute",
                    right: "100%",
                    top: "50%",
                    transform: "translateY(-50%) translateX(8px)",
                    backgroundColor: "#1f2937",
                    color: "white",
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    opacity: 0,
                    transition: "all 0.2s ease-in-out",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: "100%",
                      top: "50%",
                      transform: "translateY(-50%)",
                      border: "4px solid transparent",
                      borderLeftColor: "#1f2937",
                    }
                  }}
                >
                  Share via WhatsApp
                </Typography>
                <WhatsappActionButton phoneNum={`91${user_details.phone}`} message={message} />
              </Box>

              {/* Edit Button */}
              <Box
                sx={{
                  position: "relative",
                  "&:hover .tooltip": {
                    opacity: 1,
                    transform: "translateX(-8px)",
                  }
                }}
              >
                <Typography
                  className="tooltip"
                  variant="caption"
                  sx={{
                    position: "absolute",
                    right: "100%",
                    top: "50%",
                    transform: "translateY(-50%) translateX(8px)",
                    backgroundColor: "#1f2937",
                    color: "white",
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    opacity: 0,
                    transition: "all 0.2s ease-in-out",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: "100%",
                      top: "50%",
                      transform: "translateY(-50%)",
                      border: "4px solid transparent",
                      borderLeftColor: "#1f2937",
                    }
                  }}
                >
                  Edit Order
                </Typography>
                <IconButton
                  onClick={ReRouteToEditPage}
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: ORANGE,
                    color: "white",
                    boxShadow: "0 4px 12px rgba(249, 115, 22, 0.3)",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": { 
                      bgcolor: ORANGE_HOVER,
                      transform: "scale(1.05)",
                      boxShadow: "0 6px 20px rgba(249, 115, 22, 0.4)",
                    },
                    "&:active": {
                      transform: "scale(0.95)",
                    }
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Delete Button */}
              <Box
                sx={{
                  position: "relative",
                  "&:hover .tooltip": {
                    opacity: 1,
                    transform: "translateX(-8px)",
                  }
                }}
              >
                <Typography
                  className="tooltip"
                  variant="caption"
                  sx={{
                    position: "absolute",
                    right: "100%",
                    top: "50%",
                    transform: "translateY(-50%) translateX(8px)",
                    backgroundColor: "#1f2937",
                    color: "white",
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    opacity: 0,
                    transition: "all 0.2s ease-in-out",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: "100%",
                      top: "50%",
                      transform: "translateY(-50%)",
                      border: "4px solid transparent",
                      borderLeftColor: "#1f2937",
                    }
                  }}
                >
                  Delete Order
                </Typography>
                <IconButton
                  onClick={HandleDeleteOrder}
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: ERROR_RED,
                    color: "white",
                    boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": { 
                      bgcolor: ERROR_RED_HOVER,
                      transform: "scale(1.05)",
                      boxShadow: "0 6px 20px rgba(239, 68, 68, 0.4)",
                    },
                    "&:active": {
                      transform: "scale(0.95)",
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>

        {/* Order Summary */}
        <OrderSummary ordered_details={ordered_details} newItems={new_items} is_edit={false} />

        {/* Pricing Details */}
        <Box sx={{ maxWidth: 1000, mx: "auto", mt: 4 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderColor: alpha(ORANGE, 0.3),
              borderRadius: 2,
              backgroundColor: "#ffffff"
            }}
          >
            {/* Order breakdown */}
            {(order_info.discount > 0 || order_info.other_charges > 0) && (
              <Box sx={{ mb: 2, pb: 2, borderBottom: `1px solid ${alpha(ORANGE, 0.1)}` }}>
                <Typography variant="body1" sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <span>Order Value:</span>
                  <span>₹{order_info.total_price}</span>
                </Typography>
                
                {order_info.other_charges > 0 && (
                  <Typography variant="body1" sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <span>Other Charges:</span>
                    <span>₹{order_info.other_charges}</span>
                  </Typography>
                )}

                {order_info.discount > 0 && (
                  <Typography variant="body1" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: SUCCESS_GREEN }}>
                    <span>Discount:</span>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <span>{order_info.discount}</span>
                      <PercentIcon fontSize="small" sx={{ ml: 0.5 }} />
                    </Box>
                  </Typography>
                )}
              </Box>
            )}

            {/* Payment section */}
            {isAuthinticated ? (
              <>
                {order_info.pmt_status === "paid" ? (
                  <Typography variant="h6" sx={{ fontWeight: 700, textAlign: "right" }}>
                    Total: ₹{total_order_value}
                  </Typography>
                ) : order_info.pmt_status === "semi-paid" ? (
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
                    <Typography variant="body1">Total: ₹{total_order_value}</Typography>
                    <Typography variant="body1" sx={{ color: ERROR_RED, fontWeight: 600 }}>
                      Balance: ₹{order_info.balance_amt}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body1">Pay Now:</Typography>
                      <TextField
                        type="number"
                        value={editableTotal}
                        onChange={handleTotalChange}
                        size="small"
                        sx={{ width: 120 }}
                        InputProps={{
                          startAdornment: <Typography sx={{ mr: 0.5, color: "#64748b" }}>₹</Typography>
                        }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Total:</Typography>
                    <TextField
                      type="number"
                      value={editableTotal}
                      onChange={handleTotalChange}
                      size="small"
                      sx={{ width: 120 }}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 0.5, color: "#64748b" }}>₹</Typography>
                      }}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Typography variant="h6" sx={{ fontWeight: 700, textAlign: "right" }}>
                Total: ₹{total_order_value}
              </Typography>
            )}
          </Paper>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ 
          display: "flex", 
          flexDirection: { xs: "column", sm: "row" }, 
          justifyContent: "space-between", 
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mt: 4,
          mb: { xs: 10, sm: 4 } // Extra bottom margin on mobile for fixed button
        }}>
          {isAuthinticated && !order_info.status && (
            <Button
              variant="contained"
              onClick={HandleReadyToDeliver}
              sx={{
                backgroundColor: SUCCESS_GREEN,
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                py: 1.25,
                "&:hover": { backgroundColor: SUCCESS_GREEN_HOVER },
                boxShadow: 2
              }}
            >
              Mark Ready to Deliver
            </Button>
          )}

          {!(order_info.status && order_info.pmt_status === "paid") && isAuthinticated && (
            <Button
              variant="contained"
              onClick={handleDialogOpen}
              sx={{
                backgroundColor: ORANGE,
                fontWeight: 600,
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                py: 1.25,
                "&:hover": { backgroundColor: ORANGE_HOVER },
                boxShadow: 2
              }}
            >
              Complete Order
            </Button>
          )}
        </Box>
      </Paper>

      {/* Delivery Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 300 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, borderBottom: `1px solid ${alpha(ORANGE, 0.1)}` }}>
          Complete Delivery
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ mb: 2, color: "#64748b" }}>
            Select who received the payment:
          </Typography>
          <RadioGroup value={selectedPerson} onChange={handleRadioChange} sx={{ gap: 2 }}>
            <FormControlLabel
              value={SrinuId}
              control={<Radio sx={{ color: ORANGE, "&.Mui-checked": { color: ORANGE } }} />}
              label={
                <Box display="flex" alignItems="center" sx={{ py: 0.5 }}>
                  <Avatar alt="Srinu" src={SrinuPick} sx={{ mr: 2, width: 32, height: 32 }} />
                  <Typography sx={{ fontWeight: 500 }}>Srinu</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value={ShankarID}
              control={<Radio sx={{ color: ORANGE, "&.Mui-checked": { color: ORANGE } }} />}
              label={
                <Box display="flex" alignItems="center" sx={{ py: 0.5 }}>
                  <Avatar alt="Shankar" src={ShankarPick} sx={{ mr: 2, width: 32, height: 32 }} />
                  <Typography sx={{ fontWeight: 500 }}>Shankar</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value={NanaajiId}
              control={<Radio sx={{ color: ORANGE, "&.Mui-checked": { color: ORANGE } }} />}
              label={
                <Box display="flex" alignItems="center" sx={{ py: 0.5 }}>
                  <Avatar alt="Nanaaji" src={NanaajiPick} sx={{ mr: 2, width: 32, height: 32 }} />
                  <Typography sx={{ fontWeight: 500 }}>Nanaaji</Typography>
                </Box>
              }
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${alpha(ORANGE, 0.1)}` }}>
          <Button 
            onClick={handleDialogClose}
            sx={{ textTransform: "none", fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            onClick={HandleDeliver}
            variant="contained"
            disabled={!selectedPerson}
            sx={{
              backgroundColor: ORANGE,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              "&:hover": { backgroundColor: ORANGE_HOVER }
            }}
          >
            Complete Delivery
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog 
        open={pmtdialogOpen} 
        onClose={PaymentComponentClose}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 300 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Choose Payment Method</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Button
              onClick={() => handlePaymentClick("phonepe")}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                textTransform: "none",
                p: 2,
                borderRadius: 2,
                "&:hover": { backgroundColor: alpha(ORANGE, 0.05) }
              }}
            >
              <Avatar src="/phonepe.png" sx={{ mr: 2, width: 32, height: 32 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>PhonePe</Typography>
            </Button>
            <Button
              onClick={() => handlePaymentClick("paytm")}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                textTransform: "none",
                p: 2,
                borderRadius: 2,
                "&:hover": { backgroundColor: alpha(ORANGE, 0.05) }
              }}
            >
              <Avatar src="/paytm.ico" sx={{ mr: 2, width: 32, height: 32 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>Paytm</Typography>
            </Button>
            <Button
              onClick={() => handlePaymentClick("gpay")}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                textTransform: "none",
                p: 2,
                borderRadius: 2,
                "&:hover": { backgroundColor: alpha(ORANGE, 0.05) }
              }}
            >
              <Avatar src="/gpay.webp" sx={{ mr: 2, width: 32, height: 32 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>Google Pay</Typography>
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert 
          onClose={closeSnackbar} 
          severity={snackbarSeverity} 
          sx={{ 
            width: "100%",
            borderRadius: 2,
            fontWeight: 500
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ShowSavedOrderDetails;
